/**
 * THE LEFT RAIL, AND THE ONE THING IT MUST NOT LOSE WHEN IT COLLAPSES.
 *
 * The founder, 13 September 2026:
 *
 *   "New interface design for the Portal. And let's make sure that it can
 *    collapse the 'card' menu items on the far left such that expanded the full
 *    module names show, and collapsed only the icons show."
 *
 * COLLAPSED IS A VISUAL STATE, NOT A CONTENT STATE. The names are still there
 * when the rail is narrow — they move from the page into each link's accessible
 * name and its tooltip. A rail that actually removed them would be a column of
 * thirteen unlabelled glyphs, which a sighted person can learn and a screen
 * reader user cannot: they would hear "link, link, link". So every entry
 * carries `aria-label` in both states, and `title` so that a pointer hovering a
 * glyph gets the word back.
 *
 * THE WIDTH IS THE ONLY THING THAT CHANGES. Same order, same entries, same
 * current-marker. Somebody who collapses it while working on Compliance must
 * not have to re-find where they are.
 *
 * THE CURRENT ENTRY IS MARKED BY A BAR AND A FILL, NEVER BY COLOUR ALONE, and
 * `aria-current="page"` carries it to anybody not looking at colour at all.
 *
 * GOLD MARKS, IT NEVER SPEAKS. Nothing in this rail is gold — not the current
 * entry, not the count. Deep green on off-white, and the one orange thing is
 * the attention count, which is the colour this platform already uses for
 * "something is waiting" and is not a status anybody earned.
 *
 * WHAT REMEMBERS IT, AND WHAT DOES NOT. Collapsed-or-not lives for the visit
 * and opens expanded on the next sign-in — the same shape as the field
 * onboarding panel above the grid, and for the same reason: a person who
 * collapsed something a fortnight ago and finds it collapsed today has been
 * given a state they do not remember choosing. WHETHER IT SHOULD BE REMEMBERED
 * IS THE FOUNDER'S, and it is one line here. Recorded in the ledger rather than
 * guessed at.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { O, SANS, DISPLAY } from './ui';
import { railDestinations, currentRailKey } from './lib/rail';

const EXPANDED = 236;
const COLLAPSED = 72;

/**
 * The orange count, at rail size.
 *
 * The dashboard card carries the same number; this is the same meaning drawn
 * small enough to sit on a 40px row. Collapsed it becomes a dot on the corner
 * of the icon, because a two-digit pill inside a 72px column would push the
 * glyph off its own centre — and the number is still in the accessible name.
 */
const RailCount = ({ waiting, collapsed }) => {
  if (!waiting) return null;
  if (collapsed) {
    return (
      <span
        aria-hidden="true"
        style={{
          position: 'absolute', top: 6, right: 12, width: 9, height: 9,
          borderRadius: '50%', background: '#D68910',
          boxShadow: '0 0 0 2px rgba(255,255,255,0.92)',
        }}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      style={{
        marginLeft: 'auto', display: 'inline-flex', alignItems: 'center',
        justifyContent: 'center', minWidth: 20, height: 20, padding: '0 6px',
        borderRadius: 999, background: '#D68910', color: '#2A1B02',
        fontFamily: SANS, fontSize: 11, fontWeight: 700, flexShrink: 0,
      }}
    >{waiting > 99 ? '99+' : waiting}</span>
  );
};

function RailLink({ destination, current, collapsed, waiting }) {
  const { label, to, Icon } = destination;

  // THE ACCESSIBLE NAME IS THE SAME SENTENCE IN BOTH STATES, and it carries the
  // count — which the pill and the dot are both marked aria-hidden for, so it
  // is said once rather than twice.
  const name = waiting ? `${label}, ${waiting} waiting` : label;

  // THE SCROLL-TO-ANCHOR THAT USED TO SIT HERE IS GONE. Field onboarding was a
  // shortcut into the top of Overview and had to scroll once the router had
  // drawn it. With the grid gone the panel is that screen, so there is nothing
  // to scroll to and every entry is now a plain destination.
  return (
    <Link
      to={to}
      aria-label={name}
      aria-current={current ? 'page' : undefined}
      title={collapsed ? name : undefined}
      data-testid={`rail-${destination.key}`}
      style={{
        position: 'relative',
        display: 'flex', alignItems: 'center', gap: 12,
        height: 40, margin: '0 10px 2px',
        padding: collapsed ? 0 : '0 12px',
        justifyContent: collapsed ? 'center' : 'flex-start',
        borderRadius: 10, textDecoration: 'none',
        // THE CURRENT ENTRY IS A FILL AND A BAR, not a colour swap. Two signals,
        // because one of them is invisible to a person who cannot separate
        // these two greens.
        background: current ? 'rgba(255,255,255,0.13)' : 'transparent',
        color: current ? '#FFFFFF' : 'rgba(255,255,255,0.74)',
        fontFamily: SANS, fontSize: 13.5,
        fontWeight: current ? 600 : 500,
        whiteSpace: 'nowrap', overflow: 'hidden',
      }}
    >
      {current && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute', left: 0, top: 8, bottom: 8, width: 3,
            borderRadius: '0 3px 3px 0', background: '#FFFFFF',
          }}
        />
      )}
      <Icon size={17} strokeWidth={1.9} style={{ flexShrink: 0 }} aria-hidden="true" />
      {/* THE LABEL IS UNMOUNTED WHEN COLLAPSED, not hidden with CSS. A label
          still in the tree is one a screen reader reads in addition to the
          aria-label above it, and the person hears the module named twice. */}
      {!collapsed && <span>{label}</span>}
      <RailCount waiting={waiting} collapsed={collapsed} />
    </Link>
  );
}

export default function Rail({ cards, permissions, attention = {} }) {
  const [collapsed, setCollapsed] = useState(false);
  const { pathname } = useLocation();
  const destinations = railDestinations({ cards, permissions });
  const currentKey = currentRailKey(destinations, pathname);

  // Focus stays on the toggle across the width change, so a keyboard user is
  // not returned to the top of the document by pressing it.
  const toggle = useRef(null);
  useEffect(() => { if (toggle.current) toggle.current.blur(); }, []);

  return (
    <nav
      aria-label="Portal modules"
      data-testid="portal-rail"
      data-collapsed={collapsed ? 'true' : 'false'}
      style={{
        width: collapsed ? COLLAPSED : EXPANDED,
        flexShrink: 0,
        background: O.green,
        display: 'flex', flexDirection: 'column',
        minHeight: '100vh',
        position: 'sticky', top: 0, alignSelf: 'flex-start',
        transition: 'width 140ms ease',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: 11, padding: collapsed ? '20px 0 18px' : '20px 18px 18px',
      }}>
        <img
          src="/images/orchid-logo-green.png"
          alt=""
          width={collapsed ? 26 : 30}
          height={collapsed ? 26 : 30}
          style={{ flexShrink: 0 }}
        />
        {/* GOLD MARKS, IT NEVER SPEAKS. The mark beside the wordmark is the
            logo; the word "ORCHID PORTAL" is set in white, not in gold. */}
        {!collapsed && (
          <span style={{
            fontFamily: DISPLAY, fontSize: 15.5, letterSpacing: '0.04em',
            color: '#FFFFFF', lineHeight: 1.1,
          }}>
            ORCHID
            <span style={{
              display: 'block', fontFamily: SANS, fontSize: 8.5, fontWeight: 600,
              letterSpacing: '0.34em', color: 'rgba(255,255,255,0.62)', marginTop: 2,
            }}>PORTAL</span>
          </span>
        )}
      </div>

      <div id="portal-rail-destinations" style={{ flex: 1, overflowY: 'auto', paddingBottom: 10 }}>
        {destinations.map((d) => (
          <RailLink
            key={d.key}
            destination={d}
            current={d.key === currentKey}
            collapsed={collapsed}
            waiting={attention[d.key] || 0}
          />
        ))}
      </div>

      {/* THE TOGGLE IS AT THE BOTTOM, away from the destinations, because it is
          not one of them. A control that changes the shape of the list should
          not sit inside the list where it can be pressed by somebody reaching
          for the first module. */}
      <button
        ref={toggle}
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
        aria-controls="portal-rail-destinations"
        aria-label={collapsed ? 'Expand the module names' : 'Collapse to icons only'}
        style={{
          display: 'flex', alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: 10, height: 42, margin: '0 10px 14px',
          padding: collapsed ? 0 : '0 12px',
          borderRadius: 10, border: '1px solid rgba(255,255,255,0.16)',
          background: 'transparent', color: 'rgba(255,255,255,0.74)',
          fontFamily: SANS, fontSize: 12.5, fontWeight: 500,
          cursor: 'pointer', whiteSpace: 'nowrap', width: 'auto',
        }}
      >
        {collapsed
          ? <PanelLeftOpen size={17} strokeWidth={1.9} aria-hidden="true" />
          : <PanelLeftClose size={17} strokeWidth={1.9} aria-hidden="true" />}
        {/* THE WORD AND NOT ONLY THE ARROW — the same rule the field onboarding
            panel's Hide/Show control follows. A glyph alone says there is a
            control and not what pressing it will do. */}
        {!collapsed && <span>Collapse</span>}
      </button>
    </nav>
  );
}
